import React, { useState, useRef, useEffect } from "react";
import Dialog, { CloseDialog } from "components/Dialog";
import Button from "components/Button";
import classes from "./style.module.scss";
import {
    deleteModelGreenButtonData,
    uploadModelGreenButtonData,
    waitForModelGreenButtonHourly,
    parseGreenButtonApiResponse,
} from "utils/results/api";
import actions from "../../../_ducks/actions";
import { useDispatch } from "react-redux";
import { useStateSelector } from "utils/field";
import mixpanel from "mixpanel-browser";

const { setGreenButtonData, beginGreenButtonUpload, toggleGreenButtonUploadLoading } = actions;

const XmlUpload = ({ modelId, change, modelSave, uploadResetKey = 0, onUploadStart }) => {
    const [content, setContent] = useState("");
    const [open, setOpen] = useState(false);
    /** "importing" = file read or full upload pipeline in progress */
    const [importState, updateState] = useState("");
    const [fileName, changeFileName] = useState("");
    const fileInputRef = useRef(null);

    const dispatch = useDispatch();

    const uploadIsLoading = useStateSelector("model.greenButtonUploadLoading") ?? false;
    const sourceFileName = useStateSelector("model.modelData.utilityData.greenButtonData.sourceFileName") || "";
    const hourlyCount = useStateSelector("model.greenButtonData.hourlyGreenButtonData")?.length ?? 0;
    const utilityData = useStateSelector("model.modelData.utilityData") || {};
    const hasBaseLoadCalculation = utilityData?.hasBaseLoadCalculation || false;

    const hasUploadedGreenButtonData = !!sourceFileName || hourlyCount > 0;
    const inputDisabled = uploadIsLoading || hasUploadedGreenButtonData;

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        resetFileInput();
        updateState("");
        changeFileName("");
        setContent("");
    }, [uploadResetKey]);

    const handleFileChange = (event) => {
        updateState("importing");
        dispatch(beginGreenButtonUpload());
        onUploadStart?.();

        if (hasBaseLoadCalculation) {
            change("model.modelData.utilityData.hasBaseLoadCalculation", false);
        }
        change("model.modelData.utilityData.greenButtonData.hasTemperatureData", false);

        const file = event.target.files[0];
        const pickedName = file?.name || "";

        if (file && (pickedName.toLowerCase().endsWith(".xml") || file.type === "application/xml")) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target.result;
                setContent(text);
                changeFileName(pickedName);
                await handleContent(text, pickedName);
            };
            reader.onerror = () => {
                updateState("failed");
                dispatch(toggleGreenButtonUploadLoading(false));
                resetFileInput();
                setOpen(true);
            };
            reader.readAsText(file, "utf8");
        } else {
            updateState("failed");
            dispatch(toggleGreenButtonUploadLoading(false));
            resetFileInput();
            setOpen(true);
        }
    };

    const handleContent = async (xmlString, pickedFileName) => {
        if (!xmlString) return;
        try {
            await modelSave?.();
            await deleteModelGreenButtonData({ modelId });
            const response = await uploadModelGreenButtonData({
                modelId,
                greenButtonXmlString: xmlString,
                sourceFileName: pickedFileName,
            });
            const parsedUpload = parseGreenButtonApiResponse(response);
            if (!parsedUpload.ok) {
                throw new Error(parsedUpload.error || "Green Button upload failed");
            }
            const body = parsedUpload.body || {};
            const initialHourly = parsedUpload.hourly;
            const documentIds = parsedUpload.documentIds;
            let resolvedBody = body;

            if (documentIds.length > 0 && initialHourly.length === 0) {
                const polled = await waitForModelGreenButtonHourly({ modelId });
                if (!polled || !(polled.hourlyGreenButtonData?.length > 0)) {
                    throw new Error(
                        "Green Button data is still processing on the server. Please wait a moment and try again."
                    );
                }
                resolvedBody = polled;
            }

            const resolvedHourly = resolvedBody.hourlyGreenButtonData || [];
            if (resolvedHourly.length === 0) {
                throw new Error("Upload succeeded but no hourly Green Button data was returned.");
            }

            dispatch(
                setGreenButtonData({
                    fuelType: resolvedBody.fuelType,
                    dataSource: resolvedBody.dataSource,
                    sourceFileName: resolvedBody.sourceFileName || pickedFileName,
                    hourlyGreenButtonData: resolvedHourly,
                    combinedUtilityHeatPumpBinData: resolvedBody.combinedUtilityHeatPumpBinData,
                    documentIds: resolvedBody.documentIds ?? resolvedBody.greenButtonDocumentIds ?? documentIds,
                    hasTemperatureData: false,
                })
            );
            mixpanel.track("Green Button Upload", {
                ModelId: modelId,
                SourceFileName: pickedFileName,
                HourlyCount: resolvedHourly.length,
            });
            await modelSave?.();
            dispatch(toggleGreenButtonUploadLoading(false));
            updateState("");
            resetFileInput();
        } catch (err) {
            mixpanel.track("Green Button Upload Fail", {
                ModelId: modelId,
                SourceFileName: pickedFileName,
                Error: err?.message || "Upload failed",
            });
            // console.warn("error uploading file", err);
            dispatch(toggleGreenButtonUploadLoading(false));
            updateState("failed");
            resetFileInput();
        }
    };

    const getLabel = () => {
        if (uploadIsLoading || importState === "importing") {
            return "Loading...";
        }
        if (importState === "failed") {
            return "File Error: Upload Again";
        }
        if (hasUploadedGreenButtonData) {
            return "Uploaded";
        }
        return "Upload File";
    };

    return (
        <div>
            <div className={classes.inputWrapper}>
                <div className={classes.text}>
                    <p>Upload XML File:</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="xml-upload"
                        onChange={handleFileChange}
                        accept=".xml"
                        disabled={inputDisabled}
                    />
                    <label htmlFor="xml-upload" className={inputDisabled ? classes.labelDisabled : undefined}>
                        {getLabel()}
                    </label>
                </div>
                {sourceFileName && <p className={classes.fileName}>{sourceFileName}</p>}
            </div>
            <Dialog open={open} classes={{ paperClass: classes.dialogPaper }}>
                <CloseDialog closeActions={() => setOpen(false)} />
                <h2>Please upload a valid file</h2>
                <p>Accepted types include XML only.</p>
                <div className={classes.buttons}>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </div>
            </Dialog>
        </div>
    );
};

export default XmlUpload;
